import type { CreateDataApiKeyResponse, DataApiGrant, DataApiKey, DbTable } from '@deskworks/shared-types';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ChevronDown, ChevronRight, Pencil, Plus } from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { CodeBlock, CopyButton } from '@/components/code-block';
import { Confirm } from '@/components/confirm';
import { Field } from '@/components/field';
import { RelativeTime } from '@/components/relative-time';
import { EmptyState, ErrorState, LoadingRows } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { useApiMutation } from '@/lib/hooks';
import { t, tr } from '@/lib/i18n';
import { cn } from '@/lib/utils';

const keysKey = (workspaceId: string) => ['ws', workspaceId, 'database', 'data-api'] as const;
const basePath = '/api/data';

function grantText(g: DataApiGrant) {
  const ops = [g.read && t('read'), g.insert && t('insert'), g.update && t('update')].filter(Boolean).join(', ');
  return g.columns ? `${g.table}: ${ops} (${t('{n} columns', { n: g.columns.length })})` : `${g.table}: ${ops}`;
}

/**
 * Database → Data API (docs/features/data-api.md): keys for websites and apps that use this workspace's tables over
 * /api/data, each with read / insert / update per table or view.
 */
export function DataApiPanel({ workspaceId }: { workspaceId: string }) {
  const keys = useQuery({ queryKey: keysKey(workspaceId), queryFn: () => api.dataApi.keys(workspaceId) });
  const revoke = useApiMutation((id: string) => api.dataApi.revokeKey(id), { invalidate: [keysKey(workspaceId)], success: t('Key revoked') });
  const url = `${window.location.origin}${basePath}`;

  return (
    <Card className="min-w-0 overflow-hidden">
      <div className="flex flex-wrap items-start gap-3 border-b px-4 py-3">
        <div className="min-w-0 flex-1 basis-64">
          <h2 className="text-sm font-semibold">{t('Data API')}</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t('Websites and apps read and write the tables of this workspace over HTTP. Each key reaches only the tables you grant it. Keep it on the server, never in a browser.')}
          </p>
        </div>
        <KeyDialog workspaceId={workspaceId}>
          <Button size="sm">
            <Plus /> {t('New key')}
          </Button>
        </KeyDialog>
      </div>
      <div className="flex min-w-0 items-center gap-1 border-b bg-muted/30 py-1 pr-1 pl-4">
        <span className="text-xs text-muted-foreground">{t('Address')}</span>
        <code className="min-w-0 flex-1 truncate font-mono text-[13px]">{url}</code>
        <CopyButton value={url} label={t('Copy address')} />
      </div>
      {keys.isPending ? (
        <LoadingRows rows={2} className="p-4" />
      ) : keys.isError ? (
        <ErrorState error={keys.error} onRetry={() => void keys.refetch()} />
      ) : keys.data.length === 0 ? (
        <EmptyState compact title={t('No keys yet')} description={t('Create a key for a website or an app and choose which tables it may read or write.')} />
      ) : (
        <ul className="divide-y">
          {keys.data.map((key) => (
            <li key={key.id} className={cn('flex flex-wrap items-start gap-3 px-4 py-3 sm:flex-nowrap', key.revokedAt && 'opacity-60')}>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium">{key.name}</span>
                  <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px]">{key.prefix}…</code>
                  {key.revokedAt && <Badge tone="red">{t('Revoked')}</Badge>}
                </div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {key.grants.length === 0 ? (
                    <span className="text-xs text-muted-foreground">{t('No tables')}</span>
                  ) : (
                    key.grants.map((g) => (
                      <Badge key={g.table} tone={g.insert || g.update ? 'amber' : 'outline'}>
                        {grantText(g)}
                      </Badge>
                    ))
                  )}
                </div>
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                  {key.createdByEmail && <span>{t('Created by {email}', { email: key.createdByEmail })}</span>}
                  <span>{tr('Created {time}', { time: <RelativeTime value={key.createdAt} /> })}</span>
                  <span>{tr('Last used: {time}', { time: <RelativeTime value={key.lastUsedAt} fallback={t('never')} /> })}</span>
                  {key.revokedAt && <span>{tr('Revoked {time}', { time: <RelativeTime value={key.revokedAt} /> })}</span>}
                </div>
              </div>
              {!key.revokedAt && (
                <div className="flex gap-1">
                  <KeyDialog workspaceId={workspaceId} dataApiKey={key}>
                    <Button variant="ghost" size="sm" aria-label={t('Edit')}>
                      <Pencil />
                    </Button>
                  </KeyDialog>
                  <Confirm
                    title={t('Revoke key “{name}”?', { name: key.name })}
                    description={t('Every website or app using this key loses access with its next request. This can’t be undone.')}
                    confirmLabel={t('Revoke')}
                    destructive
                    onConfirm={() => revoke.mutateAsync(key.id)}
                  >
                    <Button variant="outline" size="sm" className="text-destructive hover:text-destructive">
                      {t('Revoke')}
                    </Button>
                  </Confirm>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

type Draft = Record<string, DataApiGrant>;

function KeyDialog({ workspaceId, dataApiKey, children }: { workspaceId: string; dataApiKey?: DataApiKey; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [draft, setDraft] = useState<Draft>({});
  const [created, setCreated] = useState<CreateDataApiKeyResponse | null>(null);
  const tables = useQuery({
    queryKey: ['ws', workspaceId, 'database', 'tables'],
    queryFn: () => api.database.tables(workspaceId),
    enabled: open,
  });
  const grants = () => Object.values(draft).filter((g) => g.read || g.insert || g.update);
  const save = useApiMutation(
    (): Promise<DataApiKey | CreateDataApiKeyResponse> =>
      dataApiKey
        ? api.dataApi.updateKey(dataApiKey.id, { name: name.trim(), grants: grants() })
        : api.dataApi.createKey(workspaceId, { name: name.trim(), grants: grants() }),
    {
      invalidate: [keysKey(workspaceId)],
      success: dataApiKey ? t('Key saved') : undefined,
      onSuccess: (res) => (dataApiKey ? setOpen(false) : setCreated(res as CreateDataApiKeyResponse)),
    },
  );

  const onOpenChange = (o: boolean) => {
    setOpen(o);
    if (o) {
      setName(dataApiKey?.name ?? '');
      setDraft(Object.fromEntries((dataApiKey?.grants ?? []).map((g) => [g.table, g])));
    } else {
      setCreated(null);
    }
  };
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim()) save.mutate();
  };
  const publicTables = (tables.data ?? []).filter((tbl) => tbl.schema === 'public');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-2xl" onInteractOutside={(e) => created && e.preventDefault()}>
        {created ? (
          <KeyCreated response={created} onDone={() => onOpenChange(false)} />
        ) : (
          <form onSubmit={onSubmit} className="grid min-w-0 gap-4">
            <DialogHeader>
              <DialogTitle>{dataApiKey ? t('Edit key') : t('New Data API key')}</DialogTitle>
              <DialogDescription>{t('A key for one website or app. It reaches only the tables and views you grant here, nothing else of Deskworks.')}</DialogDescription>
            </DialogHeader>
            <Field label={t('Name')} hint={t('So you know later what it’s for.')}>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('e.g. company website')} required autoFocus />
            </Field>
            <div className="grid min-w-0 gap-1.5">
              <Label>{t('Tables')}</Label>
              {tables.isPending ? (
                <LoadingRows rows={3} />
              ) : tables.isError ? (
                <ErrorState error={tables.error} onRetry={() => void tables.refetch()} />
              ) : publicTables.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('This database has no tables yet.')}</p>
              ) : (
                <div className="max-h-[50vh] overflow-y-auto rounded-md border">
                  <div className="sticky top-0 grid grid-cols-[minmax(0,1fr)_repeat(3,4.5rem)] gap-2 border-b bg-muted/60 px-3 py-1.5 text-xs text-muted-foreground backdrop-blur">
                    <span>{t('Table or view')}</span>
                    <span className="text-center">{t('Read')}</span>
                    <span className="text-center">{t('Insert')}</span>
                    <span className="text-center">{t('Update')}</span>
                  </div>
                  {publicTables.map((tbl) => (
                    <GrantRow
                      key={tbl.name}
                      workspaceId={workspaceId}
                      table={tbl}
                      grant={draft[tbl.name] ?? { table: tbl.name, read: false, insert: false, update: false, columns: null }}
                      onChange={(g) => setDraft((d) => ({ ...d, [tbl.name]: g }))}
                    />
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground">{t('To give out only some rows, make a view (for example only published articles) and grant the view.')}</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('Cancel')}
              </Button>
              <Button type="submit" loading={save.isPending} disabled={!name.trim()}>
                {dataApiKey ? t('Save') : t('Create key')}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function GrantRow({ workspaceId, table, grant, onChange }: { workspaceId: string; table: DbTable; grant: DataApiGrant; onChange: (g: DataApiGrant) => void }) {
  const [expanded, setExpanded] = useState(false);
  const isTable = table.kind === 'table';
  const any = grant.read || grant.insert || grant.update;
  const columns = useQuery({
    queryKey: ['ws', workspaceId, 'database', 'table', 'public', table.name, 'columns'],
    queryFn: () => api.database.table(workspaceId, 'public', table.name, { limit: 1 }),
    enabled: expanded,
  });
  const names = columns.data?.columns.map((c) => c.name) ?? [];
  const toggleColumn = (name: string, on: boolean) => {
    const current = grant.columns ?? names;
    const next = on ? [...current, name] : current.filter((c) => c !== name);
    onChange({ ...grant, columns: next.length === names.length ? null : names.filter((n) => next.includes(n)) });
  };
  const box = (field: 'read' | 'insert' | 'update', enabled = true) => (
    <span className="flex justify-center">
      <Checkbox
        checked={grant[field]}
        disabled={!enabled}
        onChange={(e) => onChange({ ...grant, [field]: e.target.checked })}
        aria-label={`${table.name}: ${field}`}
      />
    </span>
  );

  return (
    <div className="border-b last:border-b-0">
      <div className="grid grid-cols-[minmax(0,1fr)_repeat(3,4.5rem)] items-center gap-2 px-3 py-1.5 text-sm">
        <span className="flex min-w-0 items-center gap-1.5">
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            disabled={!any}
            className="flex min-w-0 items-center gap-1 text-left disabled:cursor-default"
            title={any ? t('Choose columns') : undefined}
          >
            {any ? expanded ? <ChevronDown className="size-3.5 shrink-0" /> : <ChevronRight className="size-3.5 shrink-0" /> : <span className="w-3.5 shrink-0" />}
            <span className="truncate font-mono text-[13px]">{table.name}</span>
          </button>
          {!isTable && <span className="text-[11px] text-muted-foreground">{t('view')}</span>}
          {any && grant.columns && <span className="text-[11px] text-muted-foreground">{t('{n} columns', { n: grant.columns.length })}</span>}
        </span>
        {box('read')}
        {box('insert', isTable)}
        {box('update', isTable)}
      </div>
      {expanded && any && (
        <div className="grid gap-1.5 bg-muted/30 px-3 py-2 pl-8">
          <span className="text-xs text-muted-foreground">{t('Columns the key sees (returned, filtered and sorted by):')}</span>
          {columns.isPending ? (
            <LoadingRows rows={1} />
          ) : (
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {names.map((n) => (
                <label key={n} className="flex items-center gap-1.5 font-mono text-xs">
                  <Checkbox checked={!grant.columns || grant.columns.includes(n)} onChange={(e) => toggleColumn(n, e.target.checked)} />
                  {n}
                </label>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function KeyCreated({ response, onDone }: { response: CreateDataApiKeyResponse; onDone: () => void }) {
  const url = `${window.location.origin}${response.basePath}`;
  const first = response.dataApiKey.grants.find((g) => g.read)?.table;
  const curl = `curl ${url}${first ? `/${first}?limit=10` : ''} \\\n  -H "Authorization: Bearer ${response.key}"`;
  return (
    <div className="grid min-w-0 gap-4">
      <DialogHeader>
        <DialogTitle>{t('Key created')}</DialogTitle>
        <DialogDescription>{t('“{name}” is ready. Put it into your website’s or app’s server settings.', { name: response.dataApiKey.name })}</DialogDescription>
      </DialogHeader>
      <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[13px] text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
        <AlertTriangle className="mt-0.5 size-4 shrink-0" />
        {t('Copy the key now — it’s only shown this one time. If you lose it, revoke it and create a new one.')}
      </div>
      <div className="grid min-w-0 gap-1.5">
        <Label>{t('Your key')}</Label>
        <div className="flex min-w-0 items-center gap-1 rounded-md border bg-muted/40 py-1 pr-1 pl-3">
          <code className="min-w-0 flex-1 truncate font-mono text-sm" title={response.key}>
            {response.key}
          </code>
          <CopyButton value={response.key} label={t('Copy key')} />
        </div>
      </div>
      <div className="grid min-w-0 gap-1.5">
        <Label>{t('Try it')}</Label>
        <CodeBlock value={curl} maxHeight="max-h-40" showCopy />
        <p className="text-xs text-muted-foreground">
          {t('Filters like ?status=eq.published, order=date.desc, limit and offset; POST inserts, PATCH /<id> updates.')}
        </p>
      </div>
      <DialogFooter>
        <Button onClick={onDone}>{t('Done — I’ve saved the key')}</Button>
      </DialogFooter>
    </div>
  );
}
